import { DropdownItem } from '../ui/dropdown.js';
import { TextInputType } from '../ui/text.js';
import { Expression } from './ast.js';
import { Key, Markdown } from './primitives.js';

export enum SpecialFieldKeys {
  ApplicantType = 'applicantType',
  LinkCode = 'linkCode',
  RequestedAmount = 'requestedAmount',
  ReceivedCode = 'linking.receivedCode',
}

export enum FieldType {
  Address = 'address',
  Calculated = 'calculated',
  Checkbox = 'checkbox',
  CheckboxGroup = 'checkboxGroup',
  Complex = 'complex',
  Date = 'date',
  Document = 'document',
  Dropdown = 'dropdown',
  RadioList = 'radioList',
  Text = 'text',
  Typography = 'typography',
}

export interface LabelValue {
  label: string;
  description?: string;
  value: string;
  disabled?: boolean;
  children?: Omit<LabelValue, 'children'>[];
}

export interface BaseField {
  type: FieldType;
  key: Key;
  displayName?: string;
  dynamicLogic?: Expression;
  isSensitive?: boolean;
}

// At least one must be defined
export interface Range {
  min?: number;
  max?: number;
}

interface ValidationRule {
  // If the rule expression evaluates to false, that means validation has failed
  rule: Expression;
  // If validation fails, returns this message
  message: string;
}

export interface FieldValidation {
  required: boolean;

  // If any of these rules fail, the field fails validation
  rules?: ValidationRule[];

  // If this expression resolves to true, the field *is* required
  // NOTE: this is in addition to the automated condition logic added
  // when a field has dynamic logic
  condition?: Expression;
}

export interface AddressField extends BaseField {
  type: FieldType.Address;
  copy?: string;
  validation?: FieldValidation;
  copyAddressKey?: string;
  filter?: { state: string; city?: string };
  allowUnhoused?: boolean;
}

export interface CalculatedField extends BaseField {
  type: FieldType.Calculated;
  formula: Expression;
  // If this field is set, the calculated field will display to the user.
  // Otherwise, it's only computed and stored on the backend
  display?: { copy: string; inputType?: TextInputType; validation?: FieldValidation };
}

export interface CheckboxField extends BaseField {
  type: FieldType.Checkbox;
  copy: string;
  validation?: FieldValidation;
}

export interface CheckboxGroupField extends BaseField {
  type: FieldType.CheckboxGroup;
  options: LabelValue[];
  validation?: FieldValidation;
}
export interface ComplexField extends BaseField {
  type: FieldType.Complex;
  copy: string;
  subFields: SubField[];
  validation?: FieldValidation;
  useLabelPrefix?: boolean;
}

export interface DateField extends BaseField {
  copy: string;
  props?: { helperText?: string };
  type: FieldType.Date;
  validation?: FieldValidation & {
    allowedRange?: 'today' | 'past' | 'future';
    minimumDate?: string;
    maximumDate?: string;
  };
}

export interface DocumentField extends BaseField {
  type: FieldType.Document;
  validation?: FieldValidation;
  copy?: {
    title?: string;
    description?: Markdown;
  };
}

export interface DropdownField extends BaseField {
  type: FieldType.Dropdown;
  copy: string;
  options: DropdownItem[];
  validation?: FieldValidation;
  props?:
    | { searchable?: boolean }
    | {
        childMode?: 'OnParentSelect' | 'AlwaysAvailable';
        multiple: true;
        selectAll?: true;
      };
}

export interface RadioListField extends BaseField {
  type: FieldType.RadioList;
  copy?: string;
  options: LabelValue[];
  validation?: FieldValidation;
}

export interface TextField extends BaseField {
  type: FieldType.Text;
  copy: string;
  inputType: TextInputType;
  validation?: FieldValidation;
  props?: {
    characterCounter?: { max?: number; min?: number; exact?: number };
    helperText?: string;
    inputProps?: { maxlength?: number };
    multiline?: boolean;
    rows?: number;
    tooltip?: string;
  };
}

export interface TypographyComponent extends BaseField {
  type: FieldType.Typography;
  copy: Markdown;
  props: {
    alignment?: 'left' | 'center' | 'right';
    variant:
      | 'h1'
      | 'h2'
      | 'h3'
      | 'h4'
      | 'body'
      | 'fieldLabel'
      | 'label'
      | 'largeBody'
      | 'stat'
      | 'statusBlockHeader';
  };
}

export type SubField =
  | CalculatedField
  | CheckboxField
  | DateField
  | DocumentField
  | DropdownField
  | TextField
  | TypographyComponent;

export type Field =
  | SubField
  | AddressField
  | CalculatedField
  | CheckboxField
  | CheckboxGroupField
  | ComplexField
  | DocumentField
  | RadioListField
  | TypographyComponent;
